import { useState } from "react";
import { View } from "react-native";
import {
  Dialog,
  DialogBodyText,
  DialogIcon,
  DialogTitleText,
  PrimaryButton,
  SecondaryButton,
  haptic,
  FieldMessage,
} from "@louise/moon-ui";
import {
  use2FAStatus,
  useDisable2FA,
  useDisablePasskey,
} from "@src/packages/auth/hooks/use2FA";
import { useTranslations } from "@src/packages/i18n";
import { OTPInput } from "@src/packages/form/OTPInput/OTPInput";

const padlockIcon =
  require("../../../../assets/images/you-icons/padlock.png") as number;

type Props = {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export default function Disable2FAModal({
  visible,
  onClose,
  onConfirm,
}: Props) {
  const t = useTranslations();
  const disable2FA = useDisable2FA();
  const disablePasskey = useDisablePasskey();
  // TOTP disable takes a fresh code; passkey disable re-authenticates the
  // passkey instead (no code input). Route by which factor the user has.
  const { data: status } = use2FAStatus();
  const isTotp = status?.totpEnrolled ?? false;
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTotpCode("");
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDisable = async () => {
    try {
      setError(null);
      if (isTotp) {
        await disable2FA.mutateAsync({ totpCode });
      } else {
        await disablePasskey.mutateAsync();
      }
      haptic("buzz");
      reset();
      onConfirm();
    } catch (err) {
      const msg = (err instanceof Error ? err.message : "").toLowerCase();
      const isAuthError = msg.includes("invalid") || msg.includes("not found");
      setError(
        t(
          isAuthError
            ? "you.modals.disable_2fa.error_invalid"
            : "you.modals.disable_2fa.error_generic",
        ),
      );
    }
  };

  const isValid = !isTotp || totpCode.length === 6;

  return (
    <Dialog visible={visible} onClose={handleClose} layout="centered">
      <DialogIcon
        source={padlockIcon}
        width={80}
        height={88}
        testID="disable-2fa-padlock"
      />
      <View accessibilityRole="header" style={{ marginTop: 14 }}>
        <DialogTitleText style={{ textAlign: "center" }}>
          {t("you.modals.disable_2fa.title")}
        </DialogTitleText>
      </View>
      <DialogBodyText style={{ textAlign: "center", marginBottom: 16 }}>
        {t(
          isTotp
            ? "you.modals.disable_2fa.message"
            : "you.modals.disable_2fa.passkey_message",
        )}
      </DialogBodyText>
      {isTotp && (
        <View style={{ marginBottom: error ? 8 : 16 }}>
          <OTPInput
            length={6}
            appearance="light"
            autoFocus
            hasError={!!error}
            onChange={(code) => {
              setTotpCode(code);
              setError(null);
            }}
          />
        </View>
      )}
      {error && (
        <FieldMessage centered style={{ marginBottom: 16 }}>
          {error}
        </FieldMessage>
      )}
      <View style={{ flexDirection: "row", gap: 12 }}>
        <SecondaryButton
          label={t("you.modals.disable_2fa.cancel")}
          onPress={handleClose}
          flex={1}
        />
        <PrimaryButton
          label={t("you.modals.disable_2fa.confirm")}
          onPress={handleDisable}
          loading={disable2FA.isPending || disablePasskey.isPending}
          disabled={!isValid}
          flex={1}
        />
      </View>
    </Dialog>
  );
}
